<template>
  <!-- eslint-disable vue/no-v-model-argument -->
  <div class="full-width flex-1 flex column" :class="[rootClass]">
    <q-table
      v-model:selected="selectedRef"
      :pagination="paginationRef"
      :columns="columns"
      :rows="rows"
      class="k-table no-border-radius flex-1"
      color="cyan-13"
      dense
      row-key="id"
      flat
      selection="multiple"
      hide-pagination
      hide-selected-banner
      separator="none"
      table-class="hide-scrollbar"
      v-bind="$attrs"
      :rows-per-page-options="hasPagination ? rows : [0]"
      @update:selected="$emit('update:model-value', $event)"
    >
      <template #header="localProps">
        <slot :local-props="localProps" name="header">
          <q-tr :props="localProps">
            <q-th
              v-for="(col, index) in localProps.cols"
              :key="col"
              class="relative-position"
              :class="[
                col.headerClasses,
                col.align ? `text-${col.align}` : 'text-left',
                {
                  'table-column-fit-content': ['index', 'actions', 'selection'].includes(col.name),
                },
              ]"
              :style="col.style"
            >
              <span v-if="col.name === 'selection'">
                <KCheckbox v-model="localProps.selected" dense color="space-b-100" checkbox-class="q-mr-none" />
              </span>
              <div v-else-if="col.name === 'actions'" class="full-width inline-block text-center">
                <KIcon
                  size="24px"
                  :color="isSelctedActive ? 'cool-gray-70' : 'cool-gray-50'"
                  name="menu-dots"
                  :class="{ 'cursor-pointer': isSelctedActive }"
                  @click="isSelctedActive && $emit('header-action')"
                ></KIcon>
                <slot v-if="isSelctedActive" name="header-action" />
              </div>
              <span v-else :class="col.labelClasses">
                {{ col.label }}
              </span>
              <div
                v-if="index !== localProps.cols.length - 1 && (col.separator || col.separator === undefined)"
                class="table-header-separator text-cool-gray-30 inline-block"
              ></div>
            </q-th>
          </q-tr>
        </slot>
      </template>
      <template #body="localProps">
        <slot :local-props="localProps" name="body">
          <q-tr v-if="localProps.pageIndex === 0" class="table-body-separator">
            <q-td class="separator" colspan="100%"> </q-td>
          </q-tr>
          <q-tr
            :class="{ expanded: localProps.expand }"
            :props="localProps"
            :draggable="draggable"
            @click="$emit('row-clicked', localProps.row)"
            @mouseenter="hoveredRow = localProps.row"
            @mouseleave="hoveredRow = {}"
            @dragover="draggable && $emit('row-dragover', localProps.row)"
            @dragstart="draggable && $emit('row-dragstart', localProps.row)"
            @dragend="draggable && $emit('row-dragend')"
          >
            <q-td
              v-for="col in localProps.cols"
              :key="col.name"
              :props="localProps"
              :class="col.rowClasses"
              @click="handleTdClicked(col, localProps)"
            >
              <slot :name="col.slot" :local-props="localProps" :col="col">
                <span v-if="col.name === 'selection'">
                  <KCheckbox
                    v-model="localProps.selected"
                    dense
                    color="space-b-100"
                    checkbox-class="q-mr-none"
                    :class="col.cellClasses"
                  />
                </span>
                <span v-if="col.type === 'image'">
                  <img
                    :class="['table-default-image', col.cellClasses]"
                    :alt="`${localProps.row.name} variant image`"
                    :src="col.value || defaultImage"
                  />
                </span>
                <span v-else-if="col.name === 'index'" class="k-table__index-td">
                  <span
                    v-show="!draggable || hoveredRow.id !== localProps.row.id"
                    :class="[draggable, col.cellClasses, 'q-pl-xs']"
                  >
                    {{ localProps.rowIndex + 1 }}
                  </span>
                  <KIcon
                    v-show="draggable && hoveredRow.id === localProps.row.id"
                    name="arrangement"
                    size="24px"
                    color="cool-gray-60"
                    class="cursor-grab"
                  />
                </span>
                <span v-else-if="col.type === 'money'" :class="col.cellClasses">
                  {{ formatter.format(col.value) }}</span
                >
                <span v-else-if="col.type === 'status'">
                  <KStatus :label="col.value" v-bind="localProps.row.statusBind" :class="col.cellClasses" />
                </span>
                <span v-else-if="col.type === 'expansion'" class="flex items-center">
                  <KBadge
                    :variant="(localProps.row[col.name] && localProps.row[col.name].length) || '0'"
                    :variant-icon="localProps.expand ? 'arrow-up' : 'arrow-down'"
                    :variant-inner-icon="
                      !localProps.row[col.name] || (localProps.row[col.name] && localProps.row[col.name].length <= 0)
                        ? ''
                        : '+'
                    "
                    :disable="
                      !localProps.row[col.name] || (localProps.row[col.name] && localProps.row[col.name].length <= 0)
                    "
                    class="q-mx-sm"
                    :class="{
                      'no-pointer-events':
                        !localProps.row[col.name] || (localProps.row[col.name] && localProps.row[col.name].length <= 0),
                    }"
                    @click="localProps.expand = !localProps.expand"
                  />

                  <KIcon name="eye" class="table-action-button cursor-pointer" color="inherit" size="24px" />
                </span>
                <div v-else-if="col.name === 'actions'">
                  <div class="text-center justify-center">
                    <KIcon
                      name="menu-dots"
                      class="table-action-button q-mx-sm"
                      size="24px"
                      :class="{
                        'cursor-pointer': !isSelctedActive,
                      }"
                      :color="!isSelctedActive ? 'inherit' : 'cool-gray-50'"
                      @click="!selectedRef.length && $emit('row-action', localProps.row)"
                    >
                    </KIcon>
                    <KTooltip v-if="localProps.row.actionTitle" :label="localProps.row.actionTitle" />

                    <slot v-if="!isSelctedActive" name="row-action" :local-props="localProps" />
                  </div>
                </div>
                <span v-else :class="col.cellClasses">{{ col.value }}</span>
              </slot>
            </q-td>
          </q-tr>
          <template v-if="localProps.row.variant">
            <q-tr
              v-for="(variantRow, variantIndex) in localProps.row.variant"
              v-show="localProps.expand"
              :key="variantRow"
              class="text-center variant"
              :class="{
                'last-variant':
                  localProps.row && localProps.row.variant && variantIndex + 1 === localProps.row.variant.length,
              }"
              :props="localProps"
              @click="$emit('row-clicked', variantRow)"
            >
              <q-td
                v-for="(column, columnIndex) in columns"
                :key="columnIndex"
                class="text-left"
                :class="{
                  'table-column-fit-content': ['index', 'actions', 'selection'].includes(column.name),
                }"
              >
                <span v-if="column.name === 'selection'">
                  <KCheckbox
                    v-model="selectedRef"
                    dense
                    color="space-b-100"
                    :value="variantRow"
                    checkbox-class="q-mr-none"
                  />
                </span>
                <span v-if="column.type === 'image'">
                  <img
                    class="table-default-image"
                    :alt="`${variantRow[column.name]} variant image`"
                    :src="variantRow[column.name] || defaultImage"
                  />
                </span>
                <span v-else-if="column.name === 'index'"> {{ localProps.rowIndex + 1 }}-{{ variantIndex + 1 }} </span>
                <span v-else-if="column.type === 'money'">
                  {{ formatter.format(variantRow[column.name]) }}
                </span>
                <span v-else-if="column.type === 'status'">
                  <KStatus :label="variantRow[column.name]" v-bind="variantRow.statusBind" />
                </span>
                <span v-else>{{ variantRow[column.name] }}</span>
              </q-td>
            </q-tr>
          </template>
          <q-tr class="table-body-separator">
            <q-td class="separator" colspan="100%">
              <k-divider dotted color="cool-gray-50" />
            </q-td>
          </q-tr>
        </slot>
      </template>

      <template v-if="$slots['no-data']" #no-data="{ message, icon }">
        <slot name="no-data" :message="message" :icon="icon"></slot>
      </template>
    </q-table>
    <div v-if="hasFooter" class="overflow-auto custom-scrollbar">
      <div
        class="k-table__pagination-root row full-width justify-between items-center q-pt-md q-pb-20 no-wrap"
        :class="[paginationRootClass]"
      >
        <div class="flex inline">
          <slot name="footer-action" />
        </div>
        <slot name="pagination">
          <KPagination
            v-if="hasPagination"
            v-model:per-page="paginationRef.rowsPerPage"
            v-model="paginationRef.page"
            :root-class="paginationMainClass"
            :max="pagesNumber"
            :total="paginationRef.totalCount"
            :disable="paginationDisable"
            :per-page-options="perPageOptions"
            @update:model-value="$emit('request', paginationRef)"
            @update:per-page="$emit('request', { ...paginationRef, page: 1 })"
          />
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, toRef, useAttrs, watch } from 'vue'
import defaultImage from 'src/shared/static/images/default-image.svg'

defineOptions({ name: 'KTable', inheritAttrs: false })
defineEmits([
  'request',
  'row-action',
  'update:model-value',
  'header-action',
  'row-clicked',
  'row-dragstart',
  'row-dragover',
  'row-dragend',
])
const props = defineProps({
  columns: { type: Array, required: true },
  rows: { type: Array, required: true },
  hasPagination: { type: Boolean, default: true },
  hasFooter: { type: Boolean, default: true },
  draggable: { type: Boolean, default: false },
  modelValue: { type: Array, default: () => [] },
  rootClass: { type: [Array, String, Object], default: '' },
  paginationRootClass: { type: [Array, String, Object], default: 'bg-white' },
  paginationMainClass: { type: [Array, String, Object], default: '' },
  paginationDisable: { type: Boolean, default: false },
  totalCount: { type: Number, default: 0 },
  rowsPerPage: { type: Number, default: 100 },
  headerBg: { type: String, default: 'white' },
  perPageOptions: {
    type: Array,
    default: () => [
      { label: '5', value: 5 },
      { label: '10', value: 10 },
      { label: '25', value: 25 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
    ],
  },
})

const headerBackground = toRef(() => `var(--${props.headerBg})`)

const attrs = useAttrs()
const paginationRef = ref({
  sortBy: 'desc',
  descending: false,
  page: 1,
  rowsPerPage: props.hasPagination ? props.rowsPerPage : 0,
  totalCount: props.totalCount,
  rowsNumber: 100,
})
const hoveredRow = ref({})

const selectedRef = ref(attrs.selection !== 'none' && props.modelValue.length ? props.modelValue : [])

const isSelctedActive = computed(() => selectedRef.value && selectedRef.value.length)

const pagesNumber = computed(() => Math.ceil(paginationRef.value.totalCount / paginationRef.value.rowsPerPage))
const formatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
})

const handleTdClicked = (col, localProps) => {
  if (col.emit) emit(col.emit, localProps.row)
}

watch(
  () => props.totalCount,
  (newTotalCount) => (paginationRef.value.totalCount = newTotalCount),
)
watch(
  () => props.hasPagination,
  (paginationStatus) => {
    if (paginationStatus) paginationRef.value.rowsPerPage = 10
    else paginationRef.value.rowsPerPage = 0
  },
)
watch(
  () => props.rowsPerPage,
  (newRowsPerPage) => (paginationRef.value.rowsPerPage = newRowsPerPage),
)

watch(
  () => paginationRef.value.rowsPerPage,
  (newRowsPerPage) => {
    paginationRef.value.rowsNumber = newRowsPerPage
  },
)
</script>
<style scoped lang="sass">
@import "../styles/variables.sass"
@import "../styles/mixins.sass"

.k-table
  max-width: 100%
  border-collapse: collapse
  thead :slotted(tr) th
    position: sticky
    // z-index: 12 @Kasra z-index
    z-index: 10
    top: 0
  .q-table__top
    padding: 0 !important
  td:last-child, :slotted(tr) td:last-child
    position: sticky
  th:last-child, td:last-child, :slotted(tr) th:last-child, :slotted(tr) td:last-child
    padding-left: 8px !important
    padding-right: 8px !important
    right: 0
  td:last-child, :slotted(tr) td:last-child
    z-index: 1
.k-table tr th, .k-table :slotted(tr) th
  background: var(--white)
  color: var(--cool-gray-100)
  font-size: var(--fs-13px, 13px)
.k-table tr td, .k-table :slotted(tr) td
  font-size: var(--fs-13px, 13px)
.k-table tr:not(.selected) td, .k-table :slotted(tr:not(.selected)) td
  background: var(--white)
  color: var(--cool-gray-80)
.k-table tr:not(.selected) td .table-action-button, .k-table :slotted(tr:not(.selected)) td .table-action-button
  background: var(--white)
  color: var(--cool-gray-70)
.k-table tr.selected td, .k-table :slotted(tr.selected) td
  background: var(--teal-white-20)
  color: var(--cool-gray-100)

.k-table tr:not(.variant):not(.expanded) td:first-child,
.k-table :slotted(tr:not(.variant):not(.expanded)) td:first-child
  border-radius: 12px 0 0 12px
.k-table tr:not(.variant):not(.expanded) td:last-child,
.k-table :slotted(tr:not(.variant):not(.expanded)) td:last-child
  border-radius: 0 12px 12px 0
.k-table tr:not(.variant).expanded td:first-child,
.k-table :slotted(tr:not(.variant).expanded) td:first-child
  border-radius: 12px 0 0 0
.k-table tr:not(.variant).expanded td:last-child,
.k-table :slotted(tr:not(.variant).expanded) td:last-child
  border-radius: 0 12px 0 0
.k-table tr.variant.last-variant td:first-child,
.k-table :slotted(tr.variant.last-variant) td:first-child
  border-radius: 0 0 0 12px
.k-table tr.variant.last-variant td:last-child,
.k-table :slotted(tr.variant.last-variant) td:last-child
  border-radius: 0 0 12px 0

.k-table tr.variant td:last-child,
.k-table :slotted(tr.variant) td:last-child,
.k-table tr.expanded td:last-child,
.k-table :slotted(tr.expanded) td:last-child
  border-right: 2px solid var(--space-b-50)
.k-table tr.variant td:first-child,
.k-table :slotted(tr.variant) td:first-child,
.k-table tr.expanded td:first-child,
.k-table :slotted(tr.expanded) td:first-child
  border-left: 2px solid var(--space-b-50)
.k-table tr.expanded td,
.k-table :slotted(tr.expanded) td
  border-top: 2px solid var(--space-b-50)
  border-bottom: 2px solid var(--space-b-50)
.k-table tr.last-variant td,
.k-table :slotted(tr.last-variant) td
  border-bottom: 2px solid var(--space-b-50)

.k-table tr, .k-table :slotted(tr)
  height: 66px
  position: relative
.k-table tbody tr td:not(.separator),
.k-table tbody :slotted(tr) td:not(.separator)
  height: 52px
.k-table tbody tr td:first-child:not(.separator),
.k-table tbody :slotted(tr) td:first-child:not(.separator),
.k-table thead tr th:first-child,
.k-table thead :slotted(tr) th:first-child
  padding: 4px 8px !important
.k-table tbody tr td:last-child:not(.separator),
.k-table tbody :slotted(tr) td:last-child:not(.separator),
.k-table thead tr th:last-child,
.k-table thead :slotted(tr) th:last-child
  padding: 4px 8px 4px 12px !important
.k-table tbody tr td:not(:last-child):not(:first-child):not(.separator),
.k-table tbody :slotted(tr) td:not(:last-child):not(:first-child):not(.separator),
.k-table thead tr th:not(:last-child):not(:first-child),
.k-table thead :slotted(tr) th:not(:last-child):not(:first-child)
  padding: 4px 12px 4px 12px !important

.k-table tbody tr:not(:first-child) td.separator,
.k-table tbody :slotted(tr:not(:first-child)) td.separator
  height: 25px !important
  padding-top: 0 !important
  padding-bottom: 0 !important
.k-table tbody tr:first-child td.separator,
.k-table tbody :slotted(tr:first-child) td.separator
  height: 12px !important
  padding-top: 0 !important
  padding-bottom: 0 !important
tr:last-child td.separator,
:slotted(tr:last-child) td.separator
  display: none
thead th, thead :slotted(tr) th
  border-top: 2px solid var(--cool-gray-30)
  border-bottom: 2px solid var(--cool-gray-30)
  height: 40px

.k-table tbody :slotted(tr) td:after,
.k-table tbody :slotted(tr) td:before,
.k-table tbody td:after,
.k-table tbody td:before
  display: none
  content: none
.table-default-image, :slotted(tr) .table-default-image
  width: 82px
  height: 52px
  border-radius: 8px
  object-fit: cover
.k-table .table-header-separator, .k-table :slotted(tr) .table-header-separator
  position: absolute
  right: 0
  top: 50%
  transform: translate(0, -50%)
  border-right: 1px solid var(--cool-gray-30)
  height: 12px
.k-table .table-body-separator, .k-table :slotted(tr) .table-body-separator
  height: 12px


.k-table
  @include element('pagination-root')
    border-top: 2px solid var(--cool-gray-30)
    position: sticky
    bottom: 0
    z-index: 20
    min-width: 600px
    overflow-x: auto
  :deep(.q-table__bottom--nodata)
    height: calc(100% - 41px)
    border-top: none
    justify-content: center
    align-items: center
  :deep(.q-table__progress)
    height: 2px !important
  @include element('index-td')
    width: 30px
    display: flex


:deep(.k-table)
  /* height or max-height is important */
  max-height: 100vh
  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th
    /* bg color is important for th; just specify one */
    background-color: v-bind(headerBackground)
  thead tr th
    position: sticky
    // z-index: 12 @Kasra z-index
    z-index: 10
    top: 0
  &.q-table--loading thead tr:last-child th
    /* height of all previous header rows */
    top: 48px

  tbody
    /* height of all previous header rows */
    scroll-margin-top: 48px
</style>
